import { addressGroups, markerRecords } from "./map-records";

/** Creates provider markers and a single info window on an injected map; renderInfo returns a DOM node. */
export function createMapScene({ maps, map, icons, renderInfo, onClose = () => {} }) {
  const infoWindow = new maps.InfoWindow();
  const infoClose = infoWindow.addListener("closeclick", onClose);
  const providers = new Map();
  const records = new Map();
  let bounds = new maps.LatLngBounds();
  let markerListeners = [];
  let pendingIdle;
  let clusters;
  let disposed = false;
  const bounces = new Map();

  function clear() {
    pendingIdle?.remove();
    pendingIdle = undefined;
    clusters?.dispose();
    clusters = undefined;
    bounces.forEach((timer, marker) => {
      clearTimeout(timer);
      marker.setAnimation(null);
    });
    bounces.clear();
    markerListeners.forEach((listener) => listener?.remove());
    markerListeners = [];
    providers.forEach((marker) => marker.setMap(null));
    providers.clear();
    records.clear();
    infoWindow.close();
    bounds = new maps.LatLngBounds();
  }

  function replace(results) {
    if (disposed) {
      return;
    }
    clear();
    const entries = markerRecords(results);
    entries.forEach(({ result, position }, index) => {
      if (providers.has(result.resultGUID)) {
        return;
      }
      const marker = new maps.Marker({
        map,
        position,
        title: result.label,
        zIndex: entries.length - index * 2,
        icon: { url: result.pin_image_url ?? icons.default, scaledSize: new maps.Size(50, 60) },
      });
      marker.resultGUID = result.resultGUID;
      providers.set(result.resultGUID, marker);
      records.set(marker, result);
      bounds.extend(position);
      function choose() {
        moveToTop(marker);
        openMarker(marker);
      }
      function zoom() {
        map.panTo(position);
        map.setZoom(15);
      }
      markerListeners.push(marker.addListener("click", choose), marker.addListener("dblclick", zoom));
    });
  }

  function open(record, anchor, { shouldFocus = true } = {}) {
    if (disposed) {
      return;
    }
    infoWindow.setContent(renderInfo({ result: record }));
    infoWindow.open({ map, anchor, shouldFocus });
  }

  function openCluster(markers, anchor) {
    if (disposed) {
      return;
    }
    infoWindow.setContent(renderInfo({ addressGroups: addressGroups(markers.map((marker) => records.get(marker))) }));
    infoWindow.open({ map, anchor, shouldFocus: true });
  }

  function moveToTop(marker) {
    const highest = Math.max(...[...providers.values()].map((entry) => entry.getZIndex() ?? 0));
    if (providers.size > 1 && highest) {
      marker.setZIndex(highest + 1);
    }
  }

  function openMarker(marker, { shouldFocus = true } = {}) {
    const cluster = clusters?.forMarker(marker);
    if (cluster) {
      clusters.open(cluster);
    } else {
      open(records.get(marker), marker, { shouldFocus });
    }
  }

  // Opens the result's window once the map settles, so the camera move does not close it.
  function openOnIdle(guid, { shouldFocus = true } = {}) {
    pendingIdle?.remove();
    pendingIdle = undefined;
    const marker = providers.get(guid);
    if (disposed || !marker) {
      infoWindow.close();
      return undefined;
    }
    function openSettled() {
      pendingIdle?.remove();
      pendingIdle = undefined;
      openMarker(marker, { shouldFocus });
    }
    pendingIdle = maps.event.addListenerOnce(map, "idle", openSettled);
    return marker;
  }

  function select(guid, options) {
    const marker = openOnIdle(guid, options);
    if (marker) {
      map.panTo(marker.getPosition());
      map.setZoom(15);
    }
  }

  function highlight(guid) {
    const marker = providers.get(guid);
    if (!marker || !map.getBounds()?.contains(marker.getPosition())) {
      return;
    }
    const cluster = clusters?.forMarker(marker);
    if (cluster) {
      clusters.highlight(cluster);
      return;
    }
    unhighlight(guid);
    marker.setZIndex(providers.size + 1);
    marker.setIcon({ url: icons.selected, scaledSize: new maps.Size(50, 60) });
    marker.setAnimation(maps.Animation.BOUNCE);
    function stopBounce() {
      marker.setAnimation(null);
      bounces.delete(marker);
    }
    bounces.set(marker, setTimeout(stopBounce, 750));
  }

  function unhighlight(guid) {
    clusters?.unhighlight();
    const marker = providers.get(guid);
    if (!marker) {
      return;
    }
    clearTimeout(bounces.get(marker));
    bounces.delete(marker);
    marker.setAnimation(null);
    marker.setIcon({ url: records.get(marker).pin_image_url ?? icons.default, scaledSize: new maps.Size(50, 60) });
  }

  function setClusters(manager) {
    clusters?.dispose();
    clusters = manager;
  }

  function dispose() {
    if (disposed) {
      return;
    }
    disposed = true;
    clear();
    infoClose?.remove();
  }

  return Object.freeze({
    infoWindow,
    replace,
    select,
    openOnIdle,
    open,
    openCluster,
    highlight,
    unhighlight,
    setClusters,
    dispose,
    markers: () => [...providers.values()],
    markerFor: (guid) => providers.get(guid),
    bounds: () => bounds,
  });
}
